import React from "react";
import { Space, Input, Radio, Checkbox } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { PlusCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { OptionItems } from "./SurveySlice";
import i18n from "i18n";
import {
  Sortable,
  Props as SortableProps,
} from "./sortable_choice/SortableChoiceList";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { v4 as uuidv4 } from "uuid";

const { TextArea } = Input;

interface QuestionEditRadioFormProps {
  handleRadioItemsChange: (radioItems: Array<OptionItems>) => void;
  radioItems: Array<OptionItems>;

  callParentHasCustomText: () => void;
  hasCustomText: boolean;

  callParentTextPlaceholderChange: (placeholder: string) => void;
  customTextPlaceholder: string;

  callNotPromptCheckboxChange?: (index: number, checked: boolean) => void;
  isPromptRadioButton?: boolean;

  callDefaultItemChange: (text: string) => void;
  defaultItem: string | null;

  isBranchOption?: boolean;
  callCustomEndScreenCheckboxChange?: (index: number, checked: boolean) => void;
  isExcludeChoiceLabel?: boolean;
  handleExcludeChoiceLabelChange?: (e: CheckboxChangeEvent) => void;
  formType?: string;
}

const QuestionEditRadioForm: React.FC<QuestionEditRadioFormProps> = ({
  handleRadioItemsChange,
  radioItems,
  callParentHasCustomText,
  hasCustomText,
  callParentTextPlaceholderChange,
  customTextPlaceholder,
  callNotPromptCheckboxChange,
  isPromptRadioButton,
  callDefaultItemChange,
  defaultItem,
  formType,
}) => {
  const updateDefaultItem = (newItems: OptionItems[]) => {
    const findDefaultSelectedItem = radioItems.find(
      (item) => item.name === defaultItem
    );

    if (findDefaultSelectedItem) {
      const newDefaultItem = newItems.find(
        (item) => item.dndId === findDefaultSelectedItem.dndId
      );
      if (newDefaultItem) {
        callDefaultItemChange(newDefaultItem.name);
      }
    }
  };

  const appendOption = (index: number): Array<OptionItems> => {
    let newItems = [...radioItems];

    const newItem: OptionItems = {
      name: "",
      value: "",
      dndId: uuidv4(),
    };

    if (formType === "branch" || formType === "branch_option") {
      newItem.selectId = uuidv4();
    }

    newItems.splice(index + 1, 0, newItem);
    newItems = newItems.map((item, index) => {
      return { ...item, name: `radiogroup${index + 1}` };
    });

    updateDefaultItem(newItems);

    return [...newItems];
  };

  const deleteOption = (index: number) => {
    let newItems = [...radioItems];
    const deletedItem = newItems[index];
    newItems.splice(index, 1);
    newItems = newItems.map((item, index) => {
      return { ...item, name: `radiogroup${index + 1}` };
    });

    if (deletedItem.name === defaultItem) {
      callDefaultItemChange("");
    } else {
      updateDefaultItem(newItems);
    }

    return newItems;
  };

  const handleClickPlusButton = (index: number) => {
    handleRadioItemsChange(appendOption(index));
  };

  const handleClickMinusButton = (index: number) => {
    handleRadioItemsChange(deleteOption(index));
  };

  const customTextCheckboxChange = () => {
    callParentHasCustomText();
  };

  const notPromptCheckboxChange = (index: number) => (
    e: CheckboxChangeEvent
  ) => {
    if (callNotPromptCheckboxChange) {
      callNotPromptCheckboxChange(index, e.target.checked);
    }
  };

  const customTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    callParentTextPlaceholderChange(e.target.value);
  };

  const defaultItemChange = (text: string) => {
    callDefaultItemChange(text);
  };

  const handleReorder = (reorderedItems: Array<OptionItems>) => {
    // nameを再採番
    const newItems = reorderedItems.map((item, index) => {
      return { ...item, name: `radiogroup${index + 1}` };
    });

    updateDefaultItem(newItems);

    handleRadioItemsChange(newItems);
  };

  const sortableProps: Partial<SortableProps> = {
    strategy: verticalListSortingStrategy,
    selectItems: radioItems,
    handle: true,
    onReorder: handleReorder,
    renderContent: (item, index) => {
      const isDeleteVisible = index === 0 ? "hidden" : undefined;
      return (
        <div className="question-item-horizontal">
          <Radio
            name="radiogroup"
            className="question-item-horizontal"
            style={{
              marginRight: "0",
              textAlign: "center",
            }}
            value={item.name}
            onClick={(e) => {
              if (item.name === defaultItem) {
                e.preventDefault();
                defaultItemChange("");
              }
            }}
          >
            <Input
              name={item.name}
              value={item.value}
              onChange={(e) => {
                const value = e.target.value;
                const newItem = radioItems.map((radioItem, i) =>
                  i === index ? { ...radioItem, value } : radioItem
                );
                handleRadioItemsChange(newItem);
              }}
            />
          </Radio>
          <PlusCircleOutlined
            onClick={() => handleClickPlusButton(index)}
            className="question-item-option-icon"
            style={{ color: "blue" }}
          />
          <MinusCircleOutlined
            onClick={() => handleClickMinusButton(index)}
            className="question-item-option-icon"
            style={{
              color: "red",
              visibility: isDeleteVisible,
            }}
          />
          {isPromptRadioButton && (
            <Checkbox
              checked={item.notPrompt}
              onChange={notPromptCheckboxChange(index)}
              style={{ marginLeft: "5px" }}
            >
              {i18n.t("survey.notPrompt")}
            </Checkbox>
          )}
        </div>
      );
    },
  };

  return (
    <Radio.Group
      name="radiogroup"
      onChange={(e) => {
        if (e.target.value) {
          defaultItemChange(e.target.value);
        }
      }}
      value={defaultItem}
    >
      <Space
        direction="vertical"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        <Sortable {...sortableProps} />
      </Space>
      <div className="radio-free-text">
        <Checkbox
          checked={hasCustomText}
          onChange={customTextCheckboxChange}
          style={{ marginBottom: "5px" }}
        >
          {i18n.t("survey.freeTextLabel")}
        </Checkbox>
        <TextArea
          rows={4}
          name="placeholder"
          placeholder={i18n.t("survey.editPlaceholder")}
          disabled={!hasCustomText}
          onChange={customTextChange}
          value={customTextPlaceholder}
        />
      </div>
    </Radio.Group>
  );
};

export default QuestionEditRadioForm;
